import { nextTick, onBeforeUnmount, onMounted, watch, type Ref } from 'vue';
import type { useQuoteGeneration } from '@/composables/useQuoteGeneration';
import { trackWidgetEvent } from '@/utils/widget-analytics';

type QuoteGeneration = ReturnType<typeof useQuoteGeneration>;

export interface ViewEffectsDeps {
  showResults: Ref<boolean>;
  containerRef: Ref<HTMLElement | null>;
  quoteGeneration: QuoteGeneration;
}

/**
 * While results (expanded screen) are shown, block Escape globally so the host does not
 * close its parent modal/container through listeners on document/window.
 */
const useHostEscapeGuard = (showResults: Ref<boolean>): void => {
  const preventHostEscapeWhileExpanded = (event: KeyboardEvent): void => {
    if (!showResults.value || event.key !== 'Escape') return;
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
  };

  onMounted(() => {
    // Captura global para interceptar antes de handlers del host en bubbling.
    // Registramos keydown y keyup porque algunos hosts cierran en keyup.
    document.addEventListener('keydown', preventHostEscapeWhileExpanded, true);
    document.addEventListener('keyup', preventHostEscapeWhileExpanded, true);
  });

  onBeforeUnmount(() => {
    document.removeEventListener('keydown', preventHostEscapeWhileExpanded, true);
    document.removeEventListener('keyup', preventHostEscapeWhileExpanded, true);
  });
};

/** Scroll to the top as soon as quotes are being fetched so the overlay is visible. */
const useScrollTopOnFetch = (deps: ViewEffectsDeps): void => {
  watch(
    () => deps.quoteGeneration.isFetchingQuotes.value,
    (isLoading) => {
      if (!isLoading) return;
      nextTick(() => {
        if (deps.containerRef.value) {
          deps.containerRef.value.scrollTop = 0;
        }
      });
    },
  );
};

const dispatchResultsView = (action: 'expand' | 'collapse', message: string): void => {
  const event = new CustomEvent('cotizador:results-view', { detail: { action, message } });
  document.body.dispatchEvent(event);
};

/** Also try to expand the parent container of the widget when it exists. */
const expandParentContainer = (containerRef: Ref<HTMLElement | null>): void => {
  const container = containerRef.value?.closest('[id*="cotizador-widget"]');
  const parentContainer = container?.parentElement;
  if (parentContainer && parentContainer.id === 'cotizador-widget-container') {
    parentContainer.style.width = '100%';
    parentContainer.style.maxWidth = '100%';
  }
};

/** Notify the host when the results are shown/hidden so it can expand/collapse the container. */
const useResultsViewEvents = (deps: ViewEffectsDeps): void => {
  watch(
    () => deps.showResults.value,
    (isShowing) => {
      if (!isShowing) {
        dispatchResultsView('collapse', 'El widget puede volver a su tamaño normal');
        return;
      }
      trackWidgetEvent('view_item', {});
      dispatchResultsView(
        'expand',
        'El widget necesita expandirse a pantalla completa para mostrar los resultados correctamente',
      );
      nextTick(() => expandParentContainer(deps.containerRef));
    },
    { immediate: false },
  );
};

/** Host-facing side effects of the widget views (Escape guard, scroll, results events). */
export const useViewEffects = (deps: ViewEffectsDeps): void => {
  useHostEscapeGuard(deps.showResults);
  useScrollTopOnFetch(deps);
  useResultsViewEvents(deps);
};
